import React, { useState } from 'react'
import { Box, Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import {
  MediaInsert as MediaInsertType,
  MediaInsertVariants,
} from '../../types/media'
import ReactPlayer from 'react-player/lazy'
import ClickToPlay from '../Media/ClickToPlay'
import MediaSEO from '../SEO/MediaSEO'
import { BindToFields, SuccessStoryClasses } from '../../types/contentfulAttributes'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { createContentfulClassGetter } from '../../utils/contentful'
import { useMixpanelCTAClickTracking } from '../../utils/mixpanel'

interface Props {
  insert: MediaInsertType
}

const MediaInsert: React.FC<Props> = ({ insert }) => {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()
  const isTabletAndBelow = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('lg')
  )
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const applyContentfulStyles = createContentfulClassGetter(insert.css)
  const [playVideo, setPlayVideo] = useState(false)

  let asset = null
  if (insert.assetUrl) {
    asset = insert.assetUrl
  } else if (insert.assetFile) {
    asset = insert.assetFile.file.url
  }

  const mediaSchema = {
    name: insert.schemaName,
    description: insert.schemaDescription,
    thumbnailUrl: insert.thumbnail?.file.url,
    uploadDate: insert.createdAt,
    contentUrl: asset,
    transcript: insert.transcript?.transcript ?? null,
  }

  const handleMpClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    handleMpCTAClickTracking(
      e.currentTarget.textContent,
      'Watch Video Action',
      insert.mixpanelTracking
    )
  }

  if (asset && insert.variant === MediaInsertVariants.ClickToPlay) {
    return (
      <Box onAuxClick={handleMpClick} onClick={handleMpClick}>
        <ClickToPlay
          asset={asset}
          buttonText={
            insert.codeClasses?.includes(SuccessStoryClasses.VideoButton) &&
            isTabletAndBelow
              ? t('video.clickToPlayButtonShort')
              : insert.buttonText
          }
          mediaSchema={mediaSchema}
          buttonVariant={insert.buttonVariant}
          classes={applyContentfulStyles(BindToFields.Main)}
        />
      </Box>
    )
  }

  if (asset) {
    const shouldUseLightMode = !insert.autoPlay && mediaSchema.thumbnailUrl

    return (
      <Box
        className={classes.box}
        onClick={handleMpClick}
        onAuxClick={handleMpClick}
      >
        <MediaSEO mediaSchema={mediaSchema} />
        <div className={classes.boxStreaming}>
          <ReactPlayer
            className={cx({
              [classes.playerStreaming]: !!insert.assetUrl,
            })}
            playing={playVideo || insert.autoPlay || insert.loop}
            controls={!insert.autoPlay && !insert.loop}
            light={
              shouldUseLightMode && mediaSchema.thumbnailUrl
                ? mediaSchema.thumbnailUrl
                : false
            }
            loop={insert.loop}
            muted={insert.loop || insert.autoPlay}
            onClick={() => setPlayVideo(true)}
            url={asset}
            width="100%"
            height="100%"
          />
        </div>
      </Box>
    )
  }

  return null
}
const useStyles = makeStyles()(() => ({
  box: {
    width: '100%',
    height: '100%',
  },
  boxStreaming: {
    paddingTop: '56.25%',
    position: 'relative',
    overflow: 'hidden',
    '& .react-player__preview': {
      borderRadius: '24px',
    },
  },
  playerStreaming: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
}))

export default MediaInsert
